import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Cancel, CheckCircle, Visibility } from '@mui/icons-material';
import { getSentMail } from '../../services/MailService';
import { resolveUserDisplayNames } from '../../utils/UserDisplayUtils';
import { EmailTemplateSubjects, getMailDeliveryStatusDisplay, MailDeliveryStatusIconVariant } from '../../utils/MailUtils';
import MailDetailsDialog from './MailDetailsDialog';
import { formatDateTime } from '../../utils/DiactiveUtils';

const SentMailTable = ({ email, canResolve = false }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMailId, setSelectedMailId] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const refreshItems = async () => {
    if (!email) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getSentMail(email);
      const namesById = await resolveUserDisplayNames(
        data.flatMap((item) => [item?.resolutionResolvedBy, item?.triggeredBy])
      );

      const enriched = data.map((item) => ({
        ...item,
        triggeredByName: item?.triggeredBy ? (namesById[item.triggeredBy] ?? null) : null,
        resolutionResolvedByName:
          item?.resolutionResolvedBy ? (namesById[item.resolutionResolvedBy] ?? null) : null
      }));
      setItems(enriched);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refreshItems();
    })();
    return () => {
      cancelled = true;
    };
  }, [email]);

  if (!email) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const getMailSubject = (typeKey) => EmailTemplateSubjects[typeKey] ?? typeKey ?? '—';

  const openDetails = (mailId) => {
    setSelectedMailId(mailId);
    setDetailsOpen(true);
  };

  const renderDeliveryStatusCell = (row) => {
    const isManuallyResolved = row?.resolutionStatus === "manually_resolved";
    const statusValue = row?.status;
    const { label, iconVariant } = getMailDeliveryStatusDisplay(statusValue);

    const renderedStatus = (() => {
      if (!iconVariant) {
        return label ? (
          <Typography variant="body2" component="span" sx={{ minWidth: 0 }}>
            {label}
          </Typography>
        ) : null;
      }
      const Icon = iconVariant === MailDeliveryStatusIconVariant.Error ? Cancel : CheckCircle;
      const color =
        iconVariant === MailDeliveryStatusIconVariant.Error
          ? 'error'
          : iconVariant === MailDeliveryStatusIconVariant.Primary
            ? 'primary'
            : 'success';
      return (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Icon fontSize="small" color={color} sx={{ flexShrink: 0 }} />
          <Typography variant="body2" component="span" sx={{ minWidth: 0 }}>
            {label}
          </Typography>
        </Stack>
      );
    })();
    const reason = row?.reason;
    const statusWithReasonTooltip = reason ? (
      <Tooltip title={reason} placement="top" enterDelay={300}>
        <Box sx={{ display: 'inline-flex' }}>{renderedStatus}</Box>
      </Tooltip>
    ) : renderedStatus;

    if (!isManuallyResolved) {
      return statusWithReasonTooltip;
    }

    const tooltipTitle = (
      <Stack spacing={0.5}>
        <Typography variant="caption">
          Resuelto por: {row?.resolutionResolvedByName ?? row?.resolutionResolvedBy ?? '—'}
        </Typography>
        <Typography variant="caption">{formatDateTime(row?.resolutionUpdatedAt)}</Typography>
      </Stack>
    );

    return (
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        {statusWithReasonTooltip}
        <Tooltip title={tooltipTitle} arrow>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: 'fit-content' }}>
            <CheckCircle fontSize="small" color="success" />
            <Typography variant="caption" color="success.main">
              Resuelto manualmente
            </Typography>
          </Stack>
        </Tooltip>
      </Stack>
    );
  };

  const sortedItems = [...items].sort((a, b) => {
    const aMs = a.sentAt?.getTime?.() ?? 0;
    const bMs = b.sentAt?.getTime?.() ?? 0;
    return sortDir === 'asc' ? aMs - bMs : bMs - aMs;
  });

  return (
    <TableContainer sx={{ mt: 4 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 220, minWidth: 220 }}>
              <TableSortLabel active direction={sortDir} onClick={toggleSort}>
                Fecha
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: 420, minWidth: 420 }}>Asunto</TableCell>
            <TableCell sx={{ width: 160, minWidth: 160 }}>Enviado por</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Ver</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{formatDateTime(row.sentAt)}</TableCell>
              <TableCell sx={{ width: 420, minWidth: 420 }}>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getMailSubject(row.category)}
                </Typography>
              </TableCell>
              <TableCell>{row.triggeredByName ?? row.triggeredBy ?? '—'}</TableCell>
              <TableCell>{renderDeliveryStatusCell(row)}</TableCell>
              <TableCell>
                <Tooltip title="Ver detalle">
                  <IconButton size="small" aria-label="view-mail" onClick={() => openDetails(row.id)}>
                    <Visibility color="primary" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No hay correos enviados a este paciente.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <MailDetailsDialog
        open={detailsOpen}
        mailId={selectedMailId}
        onClose={() => setDetailsOpen(false)}
        patientEmail={email}
        canResolve={canResolve}
        onResolved={refreshItems}
      />
    </TableContainer>
  );
};

export default SentMailTable;
